// lib/services/compute/typed_slot_pool.ts

import BaseSlot, { SlotState } from "./base_slot";

const IS_DEVELOPMENT = true;
 

/**
 * Behavior after a Leased slot is released. GPU producer pools keep the
 * slot in Ready so consumers can re-lease the same data (matches the
 * iOS depth-frame-twice case). CPU readback pools recycle to Idle so
 * the slot is free for the next readback.
 */
export enum PostReleasePolicy {
  KeepReady = 0,
  RecycleToIdle = 1,
}

type OrderedSlot = BaseSlot & {
  // 0 = idle and never used yet
  // >0 = idle and previously used; smaller means older
  idle_order?: number;
};

export default class TypedSlotPool<TSlot extends BaseSlot> {
  protected slots: TSlot[] = [];
  protected initialized = false;

  protected leased_slot: TSlot | null = null;
  protected latest_published_sequence: number = 0;
  protected latest_consumed_sequence: number = 0;
  protected superseded_count: number = 0;
  protected post_release_policy: PostReleasePolicy;

  // Monotonic counter for recycled idle slots.
  protected idle_order_counter: number = 1;

  constructor(post_release_policy: PostReleasePolicy = PostReleasePolicy.KeepReady) {
    this.post_release_policy = post_release_policy;
  }

  /**
   * Slot becomes Idle and gets an "age" so the oldest idle slot wins next.
   * Never-used slots remain 0 until first actual recycle.
   */
  protected mark_slot_idle(slot: TSlot): void {
    if (slot.permanent_ready) return;
    slot.state = SlotState.Idle;
    (slot as OrderedSlot).idle_order = this.idle_order_counter++;
  }

  /**
   * Mark a slot as idle but never used yet.
   */
public mark_slot_never_used(slot: TSlot): void {
    if (slot.permanent_ready) return;
    slot.state = SlotState.Idle;
    (slot as OrderedSlot).idle_order = 0;
  }

  initialize(slots: TSlot[]): boolean {
    if (this.initialized) return true;

    this.slots = slots;
    this.leased_slot = null;
    this.latest_published_sequence = 0;
    this.latest_consumed_sequence = 0;
    this.superseded_count = 0;
    this.idle_order_counter = 1;

    for (const slot of this.slots) {
      if (slot.permanent_ready) continue;

      if (slot.state === SlotState.Idle) {
        this.mark_slot_never_used(slot);
      } else {
        (slot as OrderedSlot).idle_order = Number.MAX_SAFE_INTEGER;
      }
    }

    this.initialized = true;
    return true;
  }

  

  shutdown(): void {
    this.leased_slot = null;
    for (const slot of this.slots) {
      slot.recycle();
      if (!slot.permanent_ready) {
        (slot as OrderedSlot).idle_order = Number.MAX_SAFE_INTEGER;
      }
    }
    this.slots = [];
    this.latest_published_sequence = 0;
    this.latest_consumed_sequence = 0;
    this.superseded_count = 0;
    this.idle_order_counter = 1;
    this.initialized = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Find an Idle slot to write into.
   *
   * Policy:
   * 1. Prefer an idle slot that has never been used yet (idle_order === 0)
   * 2. Otherwise choose the oldest recycled idle slot (smallest idle_order > 0)
   */
  acquire_idle_slot(): TSlot | null {
  if (!this.initialized) return null;

  let best_never_used: TSlot | null = null;
  let best_used: TSlot | null = null;
  let best_used_order = Number.POSITIVE_INFINITY;
  let best_never_used_index = -1;
  let best_used_index = -1;

  for (let i = 0; i < this.slots.length; i++) {
    const slot = this.slots[i];
    if (slot.permanent_ready) continue;
    if (slot.state !== SlotState.Idle) continue;

    const order = (slot as OrderedSlot).idle_order ?? 0;

    if (order === 0) {
      if (!best_never_used) {
        best_never_used = slot;
        best_never_used_index = i;
      }
      continue;
    }

    if (!best_used || order < best_used_order) {
      best_used = slot;
      best_used_order = order;
      best_used_index = i;
    }
  }

  const best = best_never_used ?? best_used;
  const bestIndex = best_never_used ? best_never_used_index : best_used_index;

  if (!best) {
    console.log("[TypedSlotPool] acquire_idle_slot -> none");
    return null;
  }

  const order = (best as OrderedSlot).idle_order ?? 0;
  /*console.log(
    "[TypedSlotPool] acquire_idle_slot ->",
    bestIndex,
    "order:", order,
    "neverUsed:", order === 0
  );*/

  (best as OrderedSlot).idle_order = Number.MAX_SAFE_INTEGER;
  return best;
}

  /**
   * Return the most recent Ready slot, transitioning it to Leased.
   * For permanent_ready slots, returns the slot without transitioning
   * (no lease bookkeeping). Single-consumer rule applies for non-permanent slots.
   */
  acquire_latest_ready_slot(): TSlot | null {
    if (!this.initialized) return null;

    for (const slot of this.slots) {
      if (slot.permanent_ready) {
        return slot;
      }
    }

    if (this.leased_slot !== null) {
      if (IS_DEVELOPMENT) {
        console.error(
          "[TypedSlotPool] acquire_latest_ready_slot called while a lease is held. " +
            "Release the previous lease before acquiring a new one."
        );
      }
      return null;
    }

    let best: TSlot | null = null;
    let best_seq = -1;
    for (const slot of this.slots) {
      if (slot.state !== SlotState.Ready) continue;
      if (slot.sequence > best_seq) {
        best = slot;
        best_seq = slot.sequence;
      }
    }

    if (!best) return null;

    best.state = SlotState.Leased;
    this.leased_slot = best;
    this.latest_consumed_sequence = best.sequence;
    return best;
  }

  /**
   * Release the currently leased slot.
   */
  release_leased_slot(): void {
    if (!this.initialized) return;
    if (this.leased_slot === null) return;

    const slot = this.leased_slot;
    this.leased_slot = null;

    if (slot.permanent_ready) {
      return;
    }

    if (this.post_release_policy === PostReleasePolicy.RecycleToIdle) {
      slot.recycle();
      this.mark_slot_idle(slot);
    } else {
      slot.state = SlotState.Ready;
      (slot as OrderedSlot).idle_order = Number.MAX_SAFE_INTEGER;
    }
  }

  /**
   * Mark a slot as published with sequence/timestamp.
   */
  publish_slot(slot: TSlot, sequence: number, timestampSec: number): void {
    if (!this.initialized) return;
    if (slot.permanent_ready) return;

    slot.sequence = sequence;
    slot.timestampSec = timestampSec;
    slot.state = SlotState.Ready;
    (slot as OrderedSlot).idle_order = Number.MAX_SAFE_INTEGER;

    if (sequence > this.latest_published_sequence) {
      this.latest_published_sequence = sequence;
    }

    for (const other of this.slots) {
      if (other === slot) continue;
      if (other.permanent_ready) continue;
      if (other.state !== SlotState.Ready) continue;
      if (other.sequence >= slot.sequence) continue;
      other.recycle();
      this.mark_slot_idle(other);
      this.superseded_count += 1;
    }
  }

  /**
   * Cancel an in-progress write.
   */
  cancel_slot(slot: TSlot): void {
    if (!this.initialized) return;
    if (slot.permanent_ready) return;
    slot.recycle();
    this.mark_slot_idle(slot);
  }

  for_each_slot(callback: (slot: TSlot, index: number) => void): void {
    for (let i = 0; i < this.slots.length; i++) {
      callback(this.slots[i], i);
    }
  }

  hasReadyOrLeasedSlot(): boolean {
    if (!this.initialized) return false;
    for (const slot of this.slots) {
      if (slot.permanent_ready) continue;
      if (slot.state === SlotState.Ready || slot.state === SlotState.Leased) {
        return true;
      }
    }
    return false;
  }

  getPendingCount(): number {
    if (!this.initialized) return 0;
    let count = 0;
    for (const slot of this.slots) {
      if (slot.state === SlotState.Pending) {
        count += 1;
      }
    }
    return count;
  }

  getIdleCount(): number {
    if (!this.initialized) return 0;
    let count = 0;
    for (const slot of this.slots) {
      if (slot.permanent_ready) continue;
      if (slot.state === SlotState.Idle) {
        count += 1;
      }
    }
    return count;
  }

  getLatestPublishedSequence(): number {
    return this.latest_published_sequence;
  }

  getLatestConsumedSequence(): number {
    return this.latest_consumed_sequence;
  }

  getSupersededCount(): number {
    return this.superseded_count;
  }

  getSlotCount(): number {
    return this.slots.length;
  }

  hasLease(): boolean {
    return this.leased_slot !== null;
  }

  /**
   * Read-only access to the latest Ready slot WITHOUT acquiring a lease.
   */
  peek_latest_ready_slot(): TSlot | null {
    let best: TSlot | null = null;
    let best_seq = 0;
    for (const slot of this.slots) {
      if (slot.state !== SlotState.Ready) continue;
      if (slot.sequence > best_seq) {
        best = slot;
        best_seq = slot.sequence;
      }
    }
    return best;
  }
}